/**
 * This class is responsible for managing the lifecycle and behavior of Content Objects within the application.
 */
export default class BaseObjectDirective {
    static SIGNAL_INPUT_SAVE = "response-save-input";
    static SIGNAL_INPUT_REGISTER = "register-save-input";
    static SIGNAL_CKEDITOR_REGISTER = "register-save-ckeditor";
    static SIGNAL_DIV_POPULATION_REGISTER = "register-div-populate";
    static INPUT_AUTOSAVE_KEYUP_DELAY = 2000;

    /**
     * Constructor for BaseObjectDirective.
     * @param {Object} $injector - AngularJS injector service.
     */
    constructor($injector, $scope) {
        this.$scope = $scope;
        this.initializeDependencies($injector);
        this.setupScope();
        this.setupSignalListeners();
        this.initializeComponents();

        this.autoSaveInputs = [];
        this.autoSaveCKEditors = [];
        this.autoSaveInputs = [];

        console.log(`<${this.contentObject.getUUID()}> ${this.constructor.name} init()`);

        this.isInitialized = true;
        if (this.isCMS) {
            this.$signals.emit('copyContainerCheck', this.contentObject.getUUID());
        }
    }

    /**
     * Initializes dependencies using the $injector service.
     * @param {Object} $injector - AngularJS injector service.
     */
    initializeDependencies($injector) {
        this.$config = $injector.get('$config');
        this.$signals = $injector.get('$signals');
        this.$timeout = $injector.get('$timeout');
        this.TaskDataService = $injector.get('TaskDataService');
        this.ObjectInstanceRegistrationService = $injector.get('ObjectInstanceRegistrationService');
        this.TaskSelectionService = $injector.get('TaskSelectionService');
        this.TelemetryBeaconService = $injector.has('TelemetryBeaconService')
            ? $injector.get('TelemetryBeaconService')
            : null;
        this.contentObject = this.$scope.contentObject;
        this.isPDFMode = this.$config.isPDFMode();
        this.isCMS = $injector.get('$rootScope').isCMS;
        this.isPreviewMode = this.$config.isPreviewMode();
    }

    recordTelemetry(eventType, severity, data = {}) {
        // Telemetry must never break core content object functionality
        try {
            if (this.TelemetryBeaconService && this.TelemetryBeaconService.record) {
                this.TelemetryBeaconService.record(eventType, severity, data);
            }
        } catch (e) {
            // Silently ignore telemetry errors
        }
    }

    incrementSaveCount() {
        try {
            if (this.TelemetryBeaconService) {
                this.TelemetryBeaconService.incrementSaveCount();
            }
        } catch (e) {
            // Silently ignore telemetry errors
        }
    }

    incrementSaveErrorCount() {
        try {
            if (this.TelemetryBeaconService) {
                this.TelemetryBeaconService.incrementSaveErrorCount();
            }
        } catch (e) {
            // Silently ignore telemetry errors
        }
    }

    /**
     * Sets up the scope with necessary functions and properties.
     */
    setupScope() {
        const t = this;
        this.$scope.getScope = () => t.$scope;
        this.$scope.hasPreferences = () => typeof t.$scope.preferences !== 'undefined';
        this.$scope.getPreference = (name) => t.$scope.preferencesValues?.[name];
        this.$scope.togglePreferences = this.togglePreferences.bind(this);
        this.$scope.toggleEditMode = this.toggleEditMode.bind(this);
        this.$scope.resetChanges = () => t.contentObject.resetChanges();
        this.$scope.isPDFMode = this.isPDFMode;

        this.initializeScopeProperties();
    }

    /**
     * Initializes default scope properties.
     */
    initializeScopeProperties() {
        const defaultProps = ['hasEditMode', 'hasBrowseImage', 'hasBrowseSubtitle', 'hasUploadImage'];
        defaultProps.forEach(prop => {
            if (typeof this.$scope[prop] === 'undefined') {
                this.$scope[prop] = false;
            }
        });
    }

    /**
     * Sets up signal listeners for the directive.
     */
    setupSignalListeners() {
        this.signalEvents = {};
        this.setupCMSSignals();
        this.setupAutoSaveSignals();
        this.registerSignalListeners();
    }

    /**
     * Sets up CMS-specific signals.
     */
    setupCMSSignals() {
        if (this.isCMS && !this.isPreviewMode) {
            this.signalEvents["cmsEdit_start"] = () => {
                console.log(`<${this.contentObject.getUUID()}>: cmsEdit_start`);
                this.$scope.editMode = true;
            };
            this.signalEvents["cmsEdit_end"] = () => {
                console.log(`<${this.contentObject.getUUID()}>: cmsEdit_end`);
                this.$scope.editMode = false;
            };
        }
    }

    /**
     * Sets up auto-save signals.
     */
    setupAutoSaveSignals() {
        if (!this.isCMS || this.isPreviewMode || this.isPDFMode) {
            this.setupAutoSaveCallbacks();
        }
    }

    /**
     * Sets up auto-save callbacks.
     */
    setupAutoSaveCallbacks() {
        const autoSaveCallback = this.getResponseSaveCallback('autoSave');

        // Create a debounced version of the autoSaveCallback
        const debouncedAutoSaveCallback = BaseObjectDirective.debounce(autoSaveCallback, BaseObjectDirective.INPUT_AUTOSAVE_KEYUP_DELAY);

        this.signalEvents[IB.Signals.AUTO_SAVE] = debouncedAutoSaveCallback;
        this.signalEvents[IB.Signals.BEFORE_SHEET_BLUR] = this.getResponseSaveCallback('beforeSheetBlur');

        this.$scope.$on(BaseObjectDirective.SIGNAL_INPUT_SAVE, this.getResponseSaveCallback('inputSave'));
        this.$scope.$on(BaseObjectDirective.SIGNAL_INPUT_REGISTER, (event, element) => this.registerAutoSaveInput(element));
        this.$scope.$on(BaseObjectDirective.SIGNAL_CKEDITOR_REGISTER, (event, editor) => this.registerAutoSaveCKEditor(editor));
        this.$scope.$on(BaseObjectDirective.SIGNAL_DIV_POPULATION_REGISTER, (event, div) => this.registerDivToPopulate(div));
    }

    /**
     * Registers all signal listeners.
     */
    registerSignalListeners() {
        const signalObj = {};
        for (const [signal, handler] of Object.entries(this.signalEvents)) {
            const signalId = this.$signals.on(signal, handler);
            signalObj[signal] = { id: signalId, handler };
        }

        // Page Lifecycle save triggers
        const triggerSave = (reason) => {
            if (typeof window.doNotSaveOnDestroy !== 'undefined' && window.doNotSaveOnDestroy) {
                return;
            }
            try {
                const result = this.saveResponseData(reason);
                if (result && typeof result.catch === 'function') {
                    result.catch(() => { /* swallow — best effort */ });
                }
            } catch (e) { /* swallow — best effort */ }
        };

        this._pagehideHandler = () => triggerSave('pagehide');
        this._visibilityHandler = () => {
            if (document.visibilityState === 'hidden') {
                triggerSave('visibilityHidden');
            }
        };
        this._freezeHandler = () => triggerSave('freeze');

        window.addEventListener('pagehide', this._pagehideHandler);
        document.addEventListener('visibilitychange', this._visibilityHandler);
        document.addEventListener('freeze', this._freezeHandler);

        this.$scope.$on('$destroy', async () => {
            for (const [signal, handler] of Object.entries(this.signalEvents)) {
                this.$signals.remove(signal, handler);
            }
            if (this._pagehideHandler) {
                window.removeEventListener('pagehide', this._pagehideHandler);
                this._pagehideHandler = null;
            }
            if (this._visibilityHandler) {
                document.removeEventListener('visibilitychange', this._visibilityHandler);
                this._visibilityHandler = null;
            }
            if (this._freezeHandler) {
                document.removeEventListener('freeze', this._freezeHandler);
                this._freezeHandler = null;
            }
            await this.cleanup();
        });
    }

    /**
     * Cleans up resources when the directive is destroyed.
     */
    async cleanup() {
        if (typeof window.doNotSaveOnDestroy === 'undefined' || !window.doNotSaveOnDestroy) {
            await this.saveResponseData('destroy');
        }
        this.autoSaveCKEditors = [];
        this.autoSaveInputs = [];
        this.autoPopulateDivs = [];
    }

    /**
     * Toggles the preferences mode.
     */
    togglePreferences() {
        if (this.$scope.mode === 'overview' && !this.$scope.prefsMode) {
            this.$signals.emit("closePrefMode");
        }
        this.$scope.prefsMode = !this.$scope.prefsMode;
    }

    /**
     * Toggles the edit mode.
     */
    toggleEditMode() {
        this.$scope.editMode = !this.$scope.editMode;
    }

    /**
     * Initializes various components of the directive.
     */
    initializeComponents() {
        const isCMSNotPreview = this.isCMS && !this.isPreviewMode;
        if (!isCMSNotPreview || this.isPDFMode) {
            this.fillResponseDataAsync();
        }
    }

    /**
     * Registers an object instance with the ObjectInstanceRegistrationService.
     * @param {string} objectType - The type of the object to register.
     */
    registerObjectInstance(objectType) {
        this.objectType = objectType;
        this.ObjectInstanceRegistrationService.register(objectType, this, this.contentObject.getUUID());
        this.$scope.$on('$destroy', () => {
            this.ObjectInstanceRegistrationService.unregister(objectType, this.contentObject.getUUID());
        });
    }

    /**
     * Registers an object instance with a specified name and type.
     * @param {string} objectName - The name of the object to register.
     * @param {string} objectType - The type of the object to register.
     */
    registerObjectInstanceWithName(objectName, objectType = this.objectType) {
        if (this.objectType) {
            this.ObjectInstanceRegistrationService.unregister(this.objectType, this.contentObject.getUUID());
        }
        this.ObjectInstanceRegistrationService.register(
            objectType,
            this,
            this.contentObject.getUUID(),
            objectName
        );
    }

    /**
     * Registers an input element for auto-save functionality.
     * @param {HTMLElement} element - The input element to register.
     */
    registerAutoSaveInput(element) {
        if (!this.autoSaveInputs) {
            this.autoSaveInputs = [];
        }
        this.autoSaveInputs.push(element);
        this.patchOriginalValueIntoInputField(element);
    }

    /**
     * Patches the original value into the input field.
     * @param {HTMLElement} element - The input element to patch.
     */
    patchOriginalValueIntoInputField(element) {
        const $el = $(element);
        const value = $el.val();
        const tagName = $el.prop("tagName").toLowerCase();
        const inputType = $el.attr('type') || (tagName === 'input' ? 'text' : undefined);

        if (tagName === 'input' && inputType === 'text' && value) {
            $el.attr('original-value', value);
        }
    }

    /**
     * Registers a CKEditor instance for auto-save functionality.
     * @param {Object} editor - The CKEditor instance to register.
     */
    registerAutoSaveCKEditor(editor) {
        if (!this.autoSaveCKEditors) {
            this.autoSaveCKEditors = [];
        }
        this.autoSaveCKEditors.push(editor);
    }

    /**
     * Registers a div element for population.
     * @param {HTMLElement} div - The div element to register.
     */
    registerDivToPopulate(div) {
        if (!this.autoPopulateDivs) {
            this.autoPopulateDivs = [];
        }
        this.autoPopulateDivs.push(div);
    }

    /**
     * Returns a callback function for saving response data.
     * @param {string} triggeredBy - The event that triggered the save.
     * @returns {Function} - The callback function for saving response data.
     */
    getResponseSaveCallback(triggeredBy) {
        return async () => await this.saveResponseData(triggeredBy);
    }

    /**
     * Saves the response data.
     * @param {string} triggeredBy - The event that triggered the save.
     */
    async saveResponseData(triggeredBy) {
        if (!this.isFilled || this.isPDFMode || this.$scope.isStimulus) {
            return;
        }

        try {
            let attachment = this.contentObject.getAttachement(this.$scope.$root.isPreview) || {};
            const wasAttachmentEmpty = this.isAttachmentEmpty(attachment);

            const isDirty = await this.collectResponseData(attachment, this.contentObject, triggeredBy);

            if (isDirty && !this.isAttachmentEmpty(attachment)) {
                this.advanceQuestionPartStatus();
            }

            if (isDirty) {
                this.contentObject.setAttachement(attachment);
                this.contentObject.save();
                this.contentObject.fireModelChange();
            }

            // Track successful saves for telemetry heartbeat
            try {
                if (isDirty) {
                    this.incrementSaveCount();
                }
            } catch (e) { /* telemetry must never break save */ }
        } catch (error) {
            console.error("saveResponseData failed:", error);
            // Telemetry: autosave failure
            try {
                this.recordTelemetry('autosave.failed', 'critical', {
                    contentObjectUUID: this.contentObject ? this.contentObject.getUUID() : 'unknown',
                    triggeredBy: triggeredBy || 'unknown',
                    errorMessage: error instanceof Error ? error.message.substring(0, 200) : String(error).substring(0, 200),
                });
                this.incrementSaveErrorCount();
            } catch (telemetryError) { /* telemetry must never break save */ }
            throw error;
        }
    }

    /**
     * Checks if an attachment is empty.
     * @param {Object} attachment - The attachment to check.
     * @returns {boolean} - True if the attachment is empty, false otherwise.
     */
    isAttachmentEmpty(attachment) {
        return !attachment || Object.values(attachment).every(value => value === '' || typeof value === 'undefined');
    }

    /**
     * Advances the status of the question part.
     */
    advanceQuestionPartStatus() {
        const questionPart = this.$scope.questionPart || this.$scope.$parent.questionPart;

        if (!questionPart) {
            console.warn(`<${this.contentObject.getUUID()}> Object response is dirty, but no question part known, can't set status`);
            return;
        }

        if (questionPart.getStatus() === 0) {
            console.info(`<${this.contentObject.getUUID()}> advanceQuestionPartStatus() -> setting from NOT STARTED to STARTED`);

            const updateStatus = () => {
                questionPart.setStatus(1);
                questionPart.save();
                this.$signals.emit(IB.Signals.MODELCHANGE, {
                    model: questionPart
                });
            };

            const phase = this.$scope.$root.$$phase;
            if (phase === '$apply' || phase === '$digest') {
                updateStatus();
            } else {
                this.$scope.$parent.$apply(updateStatus);
            }
        }
    }

    /**
     * Retrieves the status of the question part.
     * @returns {number} - The status of the question part.
     */
    getQuestionPartStatus() {
        const questionPart = this.$scope.questionPart || this.$scope.$parent.questionPart;
        return questionPart ? questionPart.getStatus() : -1;
    }

    /**
     * Collects response data from various inputs and editors.
     * @param {Object} attachment - The attachment object to store collected data.
     * @param {Object} contentObject - The content object associated with the response.
     * @param {string} triggeredBy - The event that triggered the data collection.
     * @returns {boolean} - True if data was collected and is dirty, false otherwise.
     */
    async collectResponseData(attachment, contentObject, triggeredBy) {
        if (this.isPDFMode) {
            return false;
        }

        let isDirty = false;

        this.autoSaveInputs.forEach(el => {
            if (this.collectResponseTextDataHelper(attachment, el, undefined, triggeredBy)) {
                isDirty = true;
            }
        });

        this.autoSaveCKEditors.forEach(editor => {
            if (this.collectResponseCKEditorDataHelper(attachment, editor, triggeredBy, contentObject)) {
                isDirty = true;
            }
        });

        isDirty = await this.responseCollect(attachment, triggeredBy) || isDirty;

        return isDirty;
    }

    /**
     * Helper function to collect data from CKEditor instances.
     * @param {Object} attachment - The attachment object to store collected data.
     * @param {Object} editor - The CKEditor instance.
     * @param {string} triggeredBy - The event that triggered the data collection.
     * @param {Object} contentObject - The content object associated with the response.
     * @returns {boolean} - True if data was collected and is dirty, false otherwise.
     */
    collectResponseCKEditorDataHelper(attachment, editor, triggeredBy, contentObject) {
        console.log("collectResponseCKEditorDataHelper", contentObject.getUUID(), editor);
        let value;
        try {
            value = editor.getData();
        } catch (e) {
            console.error("collectResponseCKEditorDataHelper: getData() threw:", e);
            // Telemetry: getData failure
            try {
                this.recordTelemetry('editor.getdata_empty', 'critical', {
                    contentObjectUUID: contentObject ? contentObject.getUUID() : 'unknown',
                    editorState: String(editor.status || editor.state || 'unknown'),
                    errorMessage: e instanceof Error ? e.message.substring(0, 200) : String(e).substring(0, 200),
                    triggeredBy: triggeredBy || 'unknown',
                });
            } catch (telemetryError) { /* Telemetry must never break autosave */ }
            return false;
        }

        let editorData = value;
        editorData = editorData === '' || editorData === '<p></p>' || editorData === '<p>&nbsp;</p>';

        // Check if editor content is empty
        const isEmpty = editor && editor.sourceElement && editor.sourceElement.id
            ? editorData
            : !angular.isDefined(value) || (angular.isDefined(value) && value.trim().length === 0);

        let isFormDirty = false;
        const state = editor.status || editor.state;
        const key = editor.name || editor.sourceElement?.id;

        const currentContent = attachment[key];
        const newContent = this.adjustImageUrls(value, 'save');

        // Only mark as dirty if content is not empty and has actually changed
        if (!isEmpty && currentContent !== newContent) {
            isFormDirty = true;
            if (typeof attachment !== 'object') {
                attachment = {};
            }
            attachment[key] = newContent;
        }
        // If data is empty, wipe from attachment (candidate might have deleted something that was there before)
        else if (isEmpty && attachment.hasOwnProperty(key)) {
            if (state === "destroyed") {
                console.log(`Not deleting data for ${key} - it is already destroyed`);
            } else if (triggeredBy === "inputSave" || triggeredBy === "beforeSheetBlur") {
                delete attachment[key];
            } else {
                console.log(`Not deleting data for ${key} - change not triggered by input event`);
            }
        }

        return isFormDirty;
    }

    /**     
     * Helper function to collect text data from form elements.
     * @param {Object} attachement - The attachment object to store collected data.
     * @param {Object} formSelectorOrElement - The form selector or element.
     * @param {string} inputSelector - The input selector.
     * @param {string} triggeredBy - The event that triggered the data collection.
     * @returns {boolean} - True if data was collected and is dirty, false otherwise.
     */
    collectResponseTextDataHelper(attachement, formSelectorOrElement, inputSelector, triggeredBy) {
        let isFormDirty = false;

        const isValueEmpty = (value, jqEl) => {
            if (value === null) return true;
            if (!angular.isDefined(value) || (angular.isDefined(value) && value.trim().length === 0)) return true;

            // Check if jqEl is defined before using it
            if (typeof jqEl !== 'undefined') {
                const origValue = jqEl.attr('original-value');
                if (typeof origValue !== 'undefined' && value === origValue) {
                    return true;
                }
            }

            return false;
        };

        const collectField = (idx, element) => {
            const jqEl = $(element);
            const id = jqEl.attr('id');
            const value = jqEl.val();
            const tagName = jqEl.prop("tagName").toLowerCase();
            let inputName = jqEl.prop("name") || id;
            inputName = inputName ? inputName.toLowerCase() : undefined;
            let inputType = jqEl.attr('type') || (tagName === 'input' ? 'text' : undefined);
            const isRichText = jqEl.attr('richtexteditor') !== undefined;
            const isTextElement = (tagName === 'textarea' || inputType === 'text') && !isRichText;
            const isCheckbox = inputType === 'checkbox';
            const isRadiobox = inputType === 'radio';
            const isSelectbox = tagName === 'select';
            const attKeyPrefix = isSelectbox ? "select" : inputType;
            const attKey = id ? id : `${attKeyPrefix}-${inputName}`;

            if (isTextElement || isRichText) {
                if (!isValueEmpty(value, jqEl)) {
                    isFormDirty = true;
                    attachement[attKey] = value;
                } else if (triggeredBy === "beforeSheetBlur" || triggeredBy === "inputSave") {
                    delete attachement[attKey];
                } else {
                    console.log(`Not deleting data for element ${id} - change not triggered by sheet blur or input event`, jqEl, triggeredBy);
                }
            } else if (isCheckbox || isRadiobox || isSelectbox) {
                if (isCheckbox) {
                    attachement[attKey] = element.checked ? "checked" : "unchecked";
                    isFormDirty = true;
                } else if (isRadiobox && element.checked) {
                    attachement[attKey] = jqEl.attr("value");
                    isFormDirty = true;
                } else if (isSelectbox) {
                    if (!isValueEmpty(value)) {
                        isFormDirty = true;
                        attachement[attKey] = value;
                    } else if (triggeredBy === "beforeSheetBlur" || triggeredBy === "inputSave") {
                        delete attachement[attKey];
                    } else {
                        console.log(`Not deleting data for element ${id} - change not triggered by sheet blur or input event`, jqEl, triggeredBy);
                    }
                }

                const dirtyAttr = jqEl.attr("data-dirty");
                if (dirtyAttr === "1") {
                    isFormDirty = true;
                    jqEl.removeAttr("data-dirty");
                }
            }
        };

        if (!angular.isDefined(inputSelector) && angular.isDefined(formSelectorOrElement)) {
            collectField(0, formSelectorOrElement[0]);
        } else if (angular.isDefined(formSelectorOrElement)) {
            $(formSelectorOrElement).find(inputSelector).each(collectField);
        } else {
            console.error("No valid combination of formSelectorOrElement/inputSelector passed");
        }

        return isFormDirty;
    }

    /**
     * Fills response data asynchronously.
     */
    fillResponseDataAsync() {
        this.isFilled = false;
        const uuid = this.$scope.contentObject.getUUID();
        console.log(`<${uuid}> fillResponseDataAsync() - starting`);

        const $signals = this.$signals;
        const contentObject = this.$scope.contentObject;
        const attachement = contentObject.getAttachement(this.$scope.$root.isPreview) || {};

        $signals.emit(IB.Signals.SHEET_WAIT_REQUEST, contentObject.getUUID(), contentObject.getObjectType());

        this.$timeout(() => {
            //console.log(`<${uuid}> fillResponseDataAsync() -> calling fillResponseData()`);

            const noCkeditors = !this.autoSaveCKEditors || this.autoSaveCKEditors.length === 0;
            const noInputs = !this.autoSaveInputs || this.autoSaveInputs.length === 0;
            const noDivs = !this.autoPopulateDivs || this.autoPopulateDivs.length === 0;
            // `fillsImmediately` (opt-in, default false): the object restores
            // through `responseFill` and has nothing to wait for, so it skips the
            // extra 300ms below, during which every save would be dropped.
            const isThereNothingToFill = !this.fillsImmediately && !this.$config.isPDFMode() && noCkeditors && noInputs && noDivs;

            console.log(`<${uuid}> fillResponseDataAsync() -> analysis:`, {
                noCkeditors, noInputs, noDivs, isThereNothingToFill
            });

            const executeAsync = async () => {
                try {
                    await this.fillResponseData(attachement, contentObject, $signals);
                } catch (error) {
                    $signals.emit(IB.Signals.SHEET_WAIT_REQUEST_CLOSED, contentObject.getUUID(), contentObject.getObjectType());
                }
            };

            if (isThereNothingToFill) {
                console.log(`<${uuid}> fillResponseDataAsync() -> nothing to fill yet, waiting for another 300ms`);
                this.$timeout(() => {
                    executeAsync();
                }, 300);
            } else {
                executeAsync();
            }
        }, 1);
    }

    /**
     * Fills response data.
     * @param {Object} attachement - The attachment data to fill.
     * @param {Object} contentObject - The content object.
     * @param {Object} $signals - The signals service.
     * @returns {Promise<void>} Promise that resolves when all data is filled.
     */
    async fillResponseData(attachement, contentObject, $signals) {
        const uuid = contentObject.getUUID();
        console.log(`<${uuid}> fillResponseData()`, attachement, this.debugId, this.isFilled,
            this.autoSaveCKEditors ? this.autoSaveCKEditors.length : 'undef',
            this.autoSaveInputs ? this.autoSaveInputs.length : 'undef',
            this.autoPopulateDivs ? this.autoPopulateDivs.length : 'undef');

        // Fill "normal" input elements
        this.autoSaveInputs?.forEach(el => this.fillResponseTextDataHelper(attachement, el, contentObject));

        // Fill DIVs (e.g. for response PDF)
        this.autoPopulateDivs?.forEach(div => this.fillResponseTextDataHelper(attachement, div));

        this.responseFill(attachement);

        // Fill CKEditors
        if (Array.isArray(this.autoSaveCKEditors) && this.autoSaveCKEditors.length > 0) {
            console.log(`<${uuid}> fillResponseData() - initializing ${this.autoSaveCKEditors.length} CKEditor(s) in parallel`);

            try {
                await Promise.all(
                    this.autoSaveCKEditors.map(editor =>
                        this.fillResponseCKEditorDataHelper(attachement, editor, contentObject)
                    )
                );
            } catch (error) {
                console.error(`<${uuid}> fillResponseData() - error initializing CKEditors:`, error);
                // Continue despite errors to maintain backward compatibility
            }
        } else {
            console.log(`<${uuid}> fillResponseData() - no CKEditors to initialize`);
        }

        $signals.emit(IB.Signals.SHEET_WAIT_REQUEST_CLOSED, contentObject.getUUID(), contentObject.getObjectType());
        this.isFilled = true;
        console.log(`<${uuid}> fillResponseData() - completed successfully`);
    }

    /**
     * Fills response data for a single CKEditor.
     * @param {Object} attachement - The attachment data.
     * @param {Object} editor - The CKEditor instance.
     * @param {Object} contentObject - The content object.
     * @returns {Promise<void>} Promise that resolves when editor is filled.
     */
    async fillResponseCKEditorDataHelper(attachement, editor, contentObject) {
        const contentObjectUUID = contentObject.getUUID();
        const editorName = editor.name ? editor.name : contentObjectUUID + '_ck5';
        console.log(`<${contentObjectUUID}> fillResponseCKEditorDataHelper()`, editorName, contentObjectUUID);

        const attachmentDataForEditor = this.adjustImageUrls(attachement[editorName], 'load');
        console.log(`<${contentObjectUUID}> fillResponseCKEditorDataHelper() data`, JSON.stringify(attachmentDataForEditor));

        if (typeof attachmentDataForEditor !== 'string') {
            console.log(`<${contentObjectUUID}> fillResponseCKEditorDataHelper() - no data to fill`);
            return;
        }

        try {
            await BaseObjectDirective.initializeEditor(editor, attachmentDataForEditor, contentObjectUUID);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Fills response text data for a single element.
     * @param {Object} attachement - The attachment data.
     * @param {HTMLElement} element - The element to fill.
     * @param {Object} contentObject - The content object.
     */
    fillResponseTextDataHelper(attachement, element, contentObject) {
        const contentObjectUUID = contentObject.getUUID();
        const escape = document.createElement('textarea');
        function escapeHTML(html) {
            escape.textContent = html;
            return escape.innerHTML;
        }

        const $el = $(element);
        const id = $el.attr('id');
        const appendIn = $el.attr('appendIn');
        const tagName = $el.prop("tagName").toLowerCase();
        const isDiv = tagName === "div";
        const inputType = $el.attr("type") || (tagName === "input" ? "text" : "");
        const isCheckbox = inputType === "checkbox";
        const isRadiobox = inputType === "radio";
        const isSelectbox = tagName === "select";
        const isTextInput = tagName === "input" && inputType === "text";
        const hasTextMCQParent = $el.closest('[objType="textMCQ"]').length > 0;
        const isRichText = $el.attr('data-is-richtext') !== '0';
        const isResponseData = $el.is('question-response-data') || isDiv;
        const inputName = $el.attr('name') || '';
        const attKeyPrefix = isSelectbox ? "select" : inputType;
        const attKey = id || `${attKeyPrefix}-${inputName.toLowerCase()}`;
        const attValueDefault = (isSelectbox || isTextInput) ? null : "";
        const attValue = attachement[attKey] || attValueDefault;
        const inputValue = $el.val();

        console.log(`<${contentObjectUUID}> fillResponseTextDataHelper() id:`, id, 'el:', element, 'attachement[attKey]:', attachement[attKey]);

        const isPDFMode = this.$config.isPDFMode();
        if (isPDFMode) {
            if (hasTextMCQParent) {
                isRichText = false;
            }
            if (!isRichText) {
                attValue = escapeHTML(attValue);
            }
        }

        if (isResponseData) {
            if (appendIn !== undefined) {
                $el.html(document.createElement(appendIn)).find(appendIn).html(attValue);
            } else {
                $el.html(attValue);
            }
            if (isPDFMode) {
                this.fixUnfinishedDesmosRenderings($el);
                this.fixUnfinishedWirisRenderings($el);
            }
        } else if (isCheckbox) {
            $el.prop('checked', attValue === "checked");
        } else if (isRadiobox) {
            $el.prop('checked', attValue === inputValue);
        } else if (isSelectbox) {
            if (attValue !== null && $el.val() !== attValue) {
                $el.val(attValue).trigger("change");
            }
        } else if (isTextInput) {
            if (isPDFMode) {
                $el.after(`<span class="text-input">${attValue}</span>`);
                $el.remove();
            } else {
                $el.val(attValue);
            }
        } else if (attValue !== null) {
            $el.val(attValue);
        }
    }

    /**
     * This method can be overridden by subclasses to modify the attachement data before it is filled into the response.
     * This is called when the response is being filled into question sheet the candidate returns to.
     * @param {*} attachement the current attachement data
     */
    responseFill(attachement) {
    }

    /**
     * This method can be overridden by subclasses to modify the attachement data before it is saved.
     * This is called when the response for the current object is being collected in order to save/persist it.
     * @param {*} attachement the current attachement data
     * @param {*} triggeredBy the event that triggered the collection
     * @returns true if the attachement has changed, false otherwise (essentially if the attachement is "dirty")
     */
    async responseCollect(attachement, triggeredBy) {
        return false;
    }

    /**
     * Static method for handling auto-save response input link.
     * @param {Object} $scope - The scope object.
     * @param {HTMLElement} element - The DOM element.
     * @param {Object} attributes - The attributes of the element.
     */
    static asfAutoSaveResponseInputLink($scope, element, attributes) {
        const tagName = $(element).prop('tagName').toUpperCase();
        var isRichText = tagName === "RICH-TEXT-EDITOR" || element.is('[richtexteditor]');
        const isInput = tagName === "INPUT" || tagName === "TEXTAREA" || tagName === "SELECT";
        const isDiv = tagName === "DIV"; // For populating HTML into DIVs from response data (e.g., for PDF rendering)

        if (isInput && !isRichText) {
            BaseObjectDirective.setupAutoSaveForInput($scope, element, attributes);
        } else if (isRichText && tagName !== "RICH-TEXT-EDITOR-CK5") {
            BaseObjectDirective.setupAutoSaveForRichText($scope, element, attributes);
        } else if (isDiv) {
            BaseObjectDirective.setupAutoPopulateForDiv($scope, element, attributes);
        } else {
            console.error("Element passed to asfResponseAutoSaveDirective is neither an INPUT nor a RICH-TEXT-EDITOR tag; can only handle these.", element);
        }
    }

    /**
     * Static method to set up auto-save for rich text editors.
     */
    static setupAutoSaveForRichText($scope, element, attributes) {
        const ckEditor = CKEDITOR.instances['cke-' + $scope.contentObject.getUUID()];
        const emitter = (e) => {
            $scope.$emit(BaseObjectDirective.SIGNAL_INPUT_SAVE);
        };

        // Create a debounced version of the emitter function
        const debouncedEmitter = BaseObjectDirective.debounce(emitter, BaseObjectDirective.INPUT_AUTOSAVE_KEYUP_DELAY);

        // Bindings
        ckEditor.on("change", debouncedEmitter);

        // Registration
        $scope.$emit(BaseObjectDirective.SIGNAL_CKEDITOR_REGISTER, ckEditor);
    }

    /**
     * Static method to set up auto-save for input elements.
     */
    static setupAutoSaveForInput($scope, element, attributes) {
        const isCheckbox = element.prop('type') === 'checkbox';
        const isRadio = element.prop('type') === 'radio';
        const isSelectbox = element.prop('tagName').toLowerCase() === 'select';

        if (isCheckbox) {
            BaseObjectDirective.wrapInput(element, true);
        } else if (isRadio) {
            BaseObjectDirective.wrapInput(element, false);
        }

        const emitter = () => {
            if (isCheckbox || isRadio || isSelectbox) {
                element.attr("data-dirty", "1");
            }
            $scope.$emit(BaseObjectDirective.SIGNAL_INPUT_SAVE);
        };

        // Use the custom debounce function
        const debouncedEmitter = BaseObjectDirective.debounce(emitter, BaseObjectDirective.INPUT_AUTOSAVE_KEYUP_DELAY);

        // Bindings
        element.on("keyup", debouncedEmitter);
        element.on("blur", emitter); // Immediate save on blur
        if (isCheckbox || isRadio || isSelectbox) {
            element.on("change", emitter);
        }

        // Registration
        $scope.$emit(BaseObjectDirective.SIGNAL_INPUT_REGISTER, element);
    }

    /**
     * Static method to set up auto-population for DIV elements.
     */
    static setupAutoPopulateForDiv($scope, element, attributes) {
        // Registration
        $scope.$emit(BaseObjectDirective.SIGNAL_DIV_POPULATION_REGISTER, element);
    }

    /**
     * Static method to wrap input elements for styling purposes.
     */
    static wrapInput(element, isCheckbox) {
        $(element).addClass('themableInput').wrap("<label></label>");
        $('<div>', {
            "class": 'inputOverlay',
            "css": { borderRadius: isCheckbox ? 3 : 7 }
        }).appendTo(element.parent()[0]);
    }

    /**
     * Static method to register the 'asfAutoSaveResponseInput' directive.
     * @param {Object} appModule - The AngularJS module to register the directive with.
     */
    static registerAutoSaveResponseInputDirective(appModule) {
        appModule.directive('asfAutoSaveResponseInput', function () {
            return {
                restrict: 'A',
                link: BaseObjectDirective.asfAutoSaveResponseInputLink
            };
        });
    }

    /**
     * Custom debounce function.
     * @param {Function} func - The function to debounce.
     * @param {number} wait - The delay in milliseconds.
     * @returns {Function} - The debounced function.
     */
    static debounce(func, wait) {
        let timeout;
        return function (...args) {
            const context = this;
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(context, args), wait);
        };
    }

    /**
     * Waits for a CKEditor instance to be ready.
     * @param {Object} editor - The CKEditor instance.
     * @returns {Promise} - Resolves when editor is ready.
     */
    static async waitForEditorReady(editor) {
        return new Promise((resolve, reject) => {
            const resolvePromise = () => {
                resolve();
            };
            // CKEditor 5
            if (editor.instanceReady || editor.state === "ready") {
                console.log("!!!!!!!!!!!!! ==>", editor);
                resolvePromise();
            }
            // CKEditor 4
            if (typeof editor.on === 'function') {
                editor.on('instanceReady', resolvePromise);
            }
            else {
                reject(new Error("Unknown editor version or structure"));
            }
        });
    }

    /**
     * Fills editor content with data.
     * @param {Object} editor - The CKEditor instance.
     * @param {string} attachmentDataForEditor - The data to fill.
     * @param {string} contentObjectUUID - The UUID of the content object.
     * @returns {Promise<void>} Promise that resolves when editor content is filled.
     */
    static async fill(editor, attachmentDataForEditor, contentObjectUUID) {
        console.log(`<${contentObjectUUID}> fillResponseCKEditorDataHelper() fill`);
        const isCKEditor5 = typeof editor.ui !== 'undefined' && typeof editor.ui.view !== 'undefined';

        if (isCKEditor5) {  // CKEditor 5
            editor.setData(attachmentDataForEditor);
            return Promise.resolve();
        } else {  // CKEditor 4
            console.log(`<${contentObjectUUID}> CKEditor 4 setData() with callback`);
            return new Promise((resolve, reject) => {
                try {
                    editor.setData(attachmentDataForEditor, {
                        callback: () => {
                            resolve();
                        }
                    });
                } catch (error) {
                    reject(error);
                }
            });
        }
    }

    /**
     * Initializes an editor with content.
     * @param {Object} editor - The CKEditor instance.
     * @param {string} attachmentDataForEditor - The data to initialize with.
     * @param {string} contentObjectUUID - The UUID of the content object.
     * @returns {Promise<void>} Promise that resolves when editor is initialized and filled.
     */
    static async initializeEditor(editor, attachmentDataForEditor, contentObjectUUID) {
        await BaseObjectDirective.waitForEditorReady(editor);
        await BaseObjectDirective.fill(editor, attachmentDataForEditor, contentObjectUUID);
    }

    /**
     * Adjusts image URLs in content based on mode.
     * @param {string} content - The content containing image URLs.
     * @param {string} mode - The mode ('save' or 'load').
     * @returns {string} - The content with adjusted URLs.
     */
    adjustImageUrls(content, mode) {
        if (!content || typeof content !== 'string' || content.indexOf('<figure') === -1) {
            return content;
        }

        const taskuuid = this.TaskSelectionService.getTaskUUID();

        const withAnyUuidRegex = /\/media\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/([^/"']+)/gi;

        if (mode === 'save') {
            return content.replace(withAnyUuidRegex, '/media/$2');
        } else {
            const isCMSorPreview = this.isCMS || this.isPreviewMode;

            if (!isCMSorPreview) {
                return content.replace(withAnyUuidRegex, '/media/$2');
            }

            const withTaskuuidRegex = new RegExp(`/media/${taskuuid}/`, 'g');
            if (withTaskuuidRegex.test(content)) {
                return content;
            }
            const withoutTaskuuidRegex = /\/media\/([^/"']+)/g;
            return content.replace(withoutTaskuuidRegex, `/media/${taskuuid}/$1`);
        }
    }
}
